import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area,
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer, 
  Legend,
  CartesianGrid
} from 'recharts'
import type { ReportFilters } from '@/lib/reports-engine'
import { useFinancialReport, useMonthlyComparisonReport } from '@/hooks/useReportsEngine'
import { useMonthlyRevenueByType } from '@/hooks/useReportsData'
import { BarChart3, TrendingUp, ArrowUpRight, ArrowDownRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'

interface FinancialReportTabProps {
  filters: ReportFilters
}

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 })

const formatCurrencyShort = (value: number) => {
  if (value >= 1000) return `R$${(value / 1000).toFixed(1)}k`
  return `R$${value.toFixed(0)}`
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border rounded-lg shadow-lg p-3 text-sm">
        <p className="font-medium mb-2">{label}</p>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2">
            <div 
              className="w-2 h-2 rounded-full" 
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-muted-foreground">{entry.name}:</span>
            <span className="font-medium">{formatCurrency(entry.value)}</span>
          </div>
        ))}
      </div>
    )
  }
  return null
}

export function FinancialReportTab({ filters }: FinancialReportTabProps) {
  const { data: financial, isLoading: loadingFinancial } = useFinancialReport(filters)
  const { data: monthlyData, isLoading: loadingMonthly } = useMonthlyRevenueByType(filters)
  const { data: comparison, isLoading: loadingComparison } = useMonthlyComparisonReport()

  const isLoading = loadingFinancial || loadingMonthly || loadingComparison

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-[300px] rounded-xl" />
        <div className="grid gap-4 lg:grid-cols-3">
          <Skeleton className="h-[200px] rounded-xl lg:col-span-2" />
          <Skeleton className="h-[200px] rounded-xl" />
        </div>
      </div>
    )
  }

  const hasData = monthlyData && monthlyData.length > 0

  return (
    <div className="space-y-6">
      {/* Main revenue chart */}
      <Card className="shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-primary" />
            Receita por Período
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasData ? (
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="hourlyGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="fixedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--success))" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="hsl(var(--success))" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                  <XAxis 
                    dataKey="month" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={formatCurrencyShort}
                    width={60}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    verticalAlign="top" 
                    height={30}
                    formatter={(value) => (
                      <span className="text-xs text-muted-foreground">{value}</span>
                    )}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="hourly" 
                    name="Por Hora"
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    fill="url(#hourlyGradient)"
                    dot={false}
                    activeDot={{ r: 4, fill: 'hsl(var(--primary))' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="fixed" 
                    name="Preço Fixo"
                    stroke="hsl(var(--success))" 
                    strokeWidth={2}
                    fill="url(#fixedGradient)"
                    dot={false}
                    activeDot={{ r: 4, fill: 'hsl(var(--success))' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[280px] flex items-center justify-center">
              <p className="text-muted-foreground">Sem dados de receita no período</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Comparison card */}
        {comparison && (
          <Card className="shadow-card lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                Comparativo Mensal
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="p-4 bg-muted/50 rounded-xl">
                  <p className="text-xs text-muted-foreground mb-1">Receita Atual</p>
                  <p className="text-2xl font-bold text-success">
                    {formatCurrency(comparison.currentRevenue)}
                  </p>
                  <div className={cn(
                    "flex items-center gap-1 mt-1 text-xs",
                    comparison.revenueGrowth >= 0 ? "text-success" : "text-destructive"
                  )}>
                    {comparison.revenueGrowth >= 0 ? (
                      <ArrowUpRight className="h-3 w-3" />
                    ) : (
                      <ArrowDownRight className="h-3 w-3" />
                    )}
                    {comparison.revenueGrowth >= 0 ? '+' : ''}{comparison.revenueGrowth}% vs anterior
                  </div>
                </div>
                <div className="p-4 bg-muted/50 rounded-xl">
                  <p className="text-xs text-muted-foreground mb-1">Horas Trabalhadas</p>
                  <p className="text-2xl font-bold">{Math.round(comparison.currentHours)}h</p>
                  <div className={cn(
                    "flex items-center gap-1 mt-1 text-xs",
                    comparison.hoursGrowth >= 0 ? "text-success" : "text-destructive"
                  )}>
                    {comparison.hoursGrowth >= 0 ? (
                      <ArrowUpRight className="h-3 w-3" />
                    ) : (
                      <ArrowDownRight className="h-3 w-3" />
                    )}
                    {comparison.hoursGrowth >= 0 ? '+' : ''}{comparison.hoursGrowth}%
                  </div>
                </div>
                <div className="p-4 bg-muted/50 rounded-xl">
                  <p className="text-xs text-muted-foreground mb-1">Projetos Concluídos</p>
                  <p className="text-2xl font-bold">{comparison.currentProjects}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Anterior: {comparison.previousProjects}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Financial summary */}
        {financial && (
          <Card className="shadow-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Resumo do Período</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Confirmado</span>
                <span className="font-semibold text-success">{formatCurrency(financial.confirmedRevenue)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Previsto</span>
                <span className="font-semibold text-warning">{formatCurrency(financial.expectedRevenue)}</span>
              </div>
              <div className="h-px bg-border my-2" />
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Total</span>
                <span className="font-bold text-lg">{formatCurrency(financial.totalRevenue)}</span>
              </div>
              <div className="flex justify-between items-center pt-2">
                <span className="text-sm text-muted-foreground">Valor/Hora Real</span>
                <span className="font-semibold">{formatCurrency(financial.realHourlyRate)}/h</span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
